/* ═══════════════════════════════════════════════════════════
   PRODUCT COMPARISON GRID — Rerender using sv-* classes
   Same visual design as the compare modal.
   The original <table> HTML is used as data source,
   then replaced with the sv-* div structure.
   ═══════════════════════════════════════════════════════════ */

if (!customElements.get('product-comparison-grid')) {
  class ProductComparisonGrid extends HTMLElement {
    constructor() {
      super();
      this.grid          = this.querySelector('.product-comparison-grid');
      this.gridContainer = this.querySelector('.product-comparison-container');
      this.showAllButton = this.querySelector('.js-show-all');

      if (this.dataset.source === 'complementary') {
        this._initComplementary();
      } else {
        this._init();
      }
    }

    // ── Complementary products via Shopify Recommendations API ──────────────
    async _initComplementary() {
      const productId = this.dataset.productId;
      const limit     = parseInt(this.dataset.limit) || 3;
      if (!productId || !this.gridContainer) return;

      // 1. Fetch complementary recommendations
      let products;
      try {
        const resp = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=complementary`);
        if (!resp.ok) return;
        ({ products } = await resp.json());
      } catch { return; }

      if (!products?.length) return;

      // 2. Fetch each product page and parse .cc-compare data-compare-key fields
      const colCount  = products.length;
      const fieldsArr = {};

      await Promise.all(products.map(async (product, prodIdx) => {
        try {
          const r = await fetch(product.url);
          if (!r.ok) return;
          const tmpl     = document.createElement('template');
          tmpl.innerHTML = await r.text();
          const sectionEl = tmpl.content.querySelector('.cc-compare');
          if (!sectionEl) return;

          const doc = document.implementation.createHTMLDocument();
          doc.documentElement.innerHTML = sectionEl.outerHTML;

          const register = (field) => {
            const key = field.dataset.compareKey;
            if (!fieldsArr[key]) fieldsArr[key] = new Array(colCount).fill(null);
            fieldsArr[key][prodIdx] = field;
          };
          doc.querySelectorAll('[data-compare-key]').forEach(register);
          doc.querySelectorAll('template').forEach(t => {
            t.content.querySelectorAll('[data-compare-key]').forEach(register);
          });
        } catch { /* skip failed product */ }
      }));

      // 3. Build data structure compatible with _buildDesktop / _buildMobile
      const isEmpty = (t) => !t || /^(n\/a|-+|—+)$/i.test(t.trim()) || t.trim() === '';
      const dash    = '<span class="sv-dash">—</span>';
      const check   = '<svg class="sv-check" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>';

      const SKIP = new Set(['compare-image', 'compare-title', 'compare-vendor', 'compare-price']);

      const data = {
        products: products.map((p, i) => {
          const imgEl  = fieldsArr['compare-image']?.[i]?.querySelector('img');
          const srcset = imgEl?.getAttribute('data-srcset') || imgEl?.getAttribute('srcset') || '';
          return {
            imgSrc:    imgEl?.src || srcset.split(' ')[0] || '',
            imgAlt:    fieldsArr['compare-title']?.[i]?.textContent.trim() || p.title,
            title:     fieldsArr['compare-title']?.[i]?.textContent.trim() || p.title,
            priceHTML: fieldsArr['compare-price']?.[i]?.innerHTML || '',
            url:       p.url,
            mediaEl:   fieldsArr['compare-image']?.[i]?.querySelector('.media') || null,
          };
        }),
        rows: [],
      };

      Object.entries(fieldsArr).forEach(([key, cols]) => {
        if (SKIP.has(key)) return;
        const allEmpty = cols.every(col => {
          if (!col) return true;
          const c = col.cloneNode(true);
          c.querySelectorAll('.compare-label').forEach(el => el.remove());
          return isEmpty(c.textContent);
        });
        if (allEmpty) return;

        const labelEl = cols.find(Boolean)?.querySelector('.compare-label');
        const label   = labelEl?.textContent.trim() ||
          key.replace(/^compare-/, '').replace(/[-_]/g, ' ');

        const cells = cols.map(col => {
          if (!col) return dash;
          const c = col.cloneNode(true);
          c.querySelectorAll('.compare-label').forEach(el => el.remove());
          const text = c.textContent.trim();
          if (/^(yes|true)$/i.test(text))  return check;
          if (isEmpty(text))                return dash;
          return c.innerHTML;
        });

        data.rows.push({ label, cells, hidden: false });
      });

      if (!data.products.length) return;

      // 4. Render using existing build methods
      const slider = this.querySelector('.product-comparison-slider');
      if (!slider) return;

      const root = document.createElement('div');
      root.className = 'sv-root';
      root.appendChild(this._buildDesktop(data));
      root.appendChild(this._buildMobile(data));

      const pillsBar = this._buildPills(data, root);
      slider.before(pillsBar);

      this.gridContainer.innerHTML = '';
      this.gridContainer.appendChild(root);
    }

    _init() {
      if (!this.grid) return;

      // ── 1. Parse existing table data ──────────────────────
      const data = this._parseTable();
      if (!data || !data.rows.length) return;

      // ── 2. Replace slider content with sv-* structure ─────
      const slider = this.querySelector('.product-comparison-slider');
      if (!slider) return;

      // Build desktop grid + mobile panels
      const root = document.createElement('div');
      root.className = 'sv-root';
      root.appendChild(this._buildDesktop(data));
      root.appendChild(this._buildMobile(data));

      // Insert pills bar before slider
      const pillsBar = this._buildPills(data, root);
      slider.before(pillsBar);

      // Replace slider contents with sv-root
      this.gridContainer.innerHTML = '';
      this.gridContainer.appendChild(root);

      // ── 3. Show all button ────────────────────────────────
      if (this.showAllButton) {
        this.showAllButton.addEventListener('click', this._handleShowAll.bind(this));
      }
    }

    // ── Parse the original <table> ──────────────────────────
    _parseTable() {
      const data = { products: [], rows: [] };

      // Products from thead
      const headRow = this.grid.querySelector('thead tr');
      if (!headRow) return null;

      headRow.querySelectorAll('th:not(.product-comparison-grid__th--left)').forEach(th => {
        const imgEl    = th.querySelector('img');
        const titleEl  = th.querySelector('.flex-auto');
        const priceEl  = th.querySelector('.price');
        const btnEl    = th.querySelector('.btn--primary');
        const mediaEl  = th.querySelector('.media');

        // Theme uses lazy loading — src may be empty, use data-src or srcset
        let imgSrc = imgEl?.src || '';
        if (!imgSrc || imgSrc.includes('blank') || imgSrc.endsWith('/')) {
          imgSrc = imgEl?.dataset?.src
            || imgEl?.getAttribute('data-srcset')?.split(' ')[0]
            || imgEl?.getAttribute('srcset')?.split(' ')[0]
            || '';
        }

        data.products.push({
          imgSrc,
          imgAlt:   imgEl?.alt || '',
          title:    titleEl?.textContent.trim() || '',
          priceHTML: priceEl?.outerHTML || '',
          url:      btnEl?.href || '#',
          mediaEl:  mediaEl,
        });
      });

      if (!data.products.length) return null;

      // Rows from tbody — collect pairs: [label-row, data-row]
      const tbodyRows = [...this.grid.querySelectorAll('tbody tr')];
      let i = 0;
      while (i < tbodyRows.length) {
        const tr = tbodyRows[i];
        const isLabelRow = tr.querySelector('.product-comparison-grid__th--top');

        if (isLabelRow) {
          const sticky = tr.querySelector('.product-comparison-grid__sticky');
          const label  = sticky ? sticky.textContent.trim() : tr.textContent.trim();
          const dataRow = tbodyRows[i + 1];

          if (dataRow) {
            const cells = [...dataRow.querySelectorAll('td')].map(td => td.innerHTML.trim());
            const hidden = tr.classList.contains('js-hidden') || dataRow.classList.contains('js-hidden');
            // Skip row if ALL cells are empty / dash / empty field text
            const allEmpty = cells.every(c => !c || /^(-|—|&mdash;)$/.test(c.replace(/<[^>]+>/g, '').trim()) || c.replace(/<[^>]+>/g, '').trim() === '');
            if (!allEmpty) data.rows.push({ label, cells, hidden });
            i += 2; // skip both rows
          } else {
            i++;
          }
        } else {
          // Desktop-only data row (no preceding label row visible on mobile)
          const labelTh = tr.querySelector('.product-comparison-grid__th--left');
          const label   = labelTh ? labelTh.textContent.trim().replace(/:$/, '') : '';
          const cells   = [...tr.querySelectorAll('td')].map(td => td.innerHTML.trim());
          const hidden  = tr.classList.contains('js-hidden');
          const allEmpty = cells.every(c => !c || /^(-|—|&mdash;)$/.test(c.replace(/<[^>]+>/g, '').trim()) || c.replace(/<[^>]+>/g, '').trim() === '');
          if (!allEmpty && (label || cells.length)) data.rows.push({ label, cells, hidden });
          i++;
        }
      }

      return data;
    }

    // ── Pills ───────────────────────────────────────────────
    _buildPills(data, root) {
      const bar = document.createElement('div');
      bar.className = 'sv-pills';

      const lbl = document.createElement('span');
      lbl.className   = 'sv-pills__label';
      lbl.textContent = 'Show';
      bar.appendChild(lbl);

      data.rows.forEach((row, idx) => {
        if (!row.label) return;
        const pill = document.createElement('button');
        pill.type        = 'button';
        pill.className   = 'sv-pill is-active';
        pill.textContent = row.label;

        pill.setAttribute('aria-pressed', 'true');
        pill.addEventListener('click', () => {
          const on = pill.classList.toggle('is-active');
          pill.setAttribute('aria-pressed', String(on));
          // Toggle both desktop row and mobile rows
          root.querySelectorAll(`[data-row-idx="${idx}"]`).forEach(el => {
            el.hidden = !on;
          });
        });

        bar.appendChild(pill);
      });

      return bar;
    }

    // ── Desktop grid ────────────────────────────────────────
    _buildDesktop(data) {
      const colCount = data.products.length;

      const grid = document.createElement('div');
      grid.className = `sv-grid sv-grid--${colCount}col`;

      const body = document.createElement('div');
      body.className = 'sv-grid__body';
      body.setAttribute('role', 'table');
      body.setAttribute('aria-label', 'Product comparison');

      const thead = document.createElement('div');
      thead.setAttribute('role', 'rowgroup');

      // ── Product header ────────────────────────────────────
      const header = document.createElement('div');
      header.className = 'sv-header';
      header.setAttribute('role', 'row');

      const corner = document.createElement('div');
      corner.className = 'sv-corner';
      corner.setAttribute('role', 'columnheader');
      corner.setAttribute('aria-label', 'Product attribute');
      header.appendChild(corner);

      data.products.forEach(p => {
        const cell = document.createElement('div');
        cell.className = 'sv-prod';
        cell.setAttribute('role', 'columnheader');
        cell.setAttribute('aria-label', p.title || '');

        const inner = document.createElement('div');
        inner.className = 'sv-prod__inner';

        // Image
        if (p.mediaEl) {
          const imgWrap = document.createElement('div');
          imgWrap.className = 'sv-prod__img';
          const clonedMedia = p.mediaEl.cloneNode(true);
          clonedMedia.querySelectorAll('img').forEach(img => { img.alt = p.title || ''; });
          imgWrap.appendChild(clonedMedia);
          inner.appendChild(imgWrap);
        } else if (p.imgSrc) {
          const imgWrap = document.createElement('div');
          imgWrap.className = 'sv-prod__img';
          const img = document.createElement('img');
          img.src = p.imgSrc; img.alt = p.imgAlt;
          const med = document.createElement('div');
          med.className = 'media'; med.style.paddingTop = '100%';
          med.appendChild(img); imgWrap.appendChild(med);
          inner.appendChild(imgWrap);
        }

        const info = document.createElement('div');
        info.className = 'sv-prod__info';

        if (p.title) {
          const a = document.createElement('a');
          a.className   = 'sv-prod__title';
          a.href        = p.url;
          a.textContent = p.title;
          info.appendChild(a);
        }

        if (p.priceHTML) {
          const pr = document.createElement('div');
          pr.className = 'sv-prod__price';
          pr.innerHTML = p.priceHTML;
          info.appendChild(pr);
        }

        // View details btn
        const btn = document.createElement('a');
        btn.href      = p.url;
        btn.className = 'btn btn--primary sv-prod__btn';
        btn.textContent = 'View details';
        info.appendChild(btn);

        inner.appendChild(info);
        cell.appendChild(inner);
        header.appendChild(cell);
      });

      thead.appendChild(header);
      body.appendChild(thead);

      const tbody = document.createElement('div');
      tbody.setAttribute('role', 'rowgroup');

      // ── Data rows ─────────────────────────────────────────
      data.rows.forEach((row, idx) => {
        const rowEl = document.createElement('div');
        rowEl.className       = 'sv-row';
        rowEl.setAttribute('role', 'row');
        rowEl.dataset.rowIdx  = idx;
        if (row.hidden) rowEl.hidden = true;

        const lc = document.createElement('div');
        lc.className   = 'sv-label';
        lc.setAttribute('role', 'rowheader');
        lc.textContent = row.label;
        rowEl.appendChild(lc);

        row.cells.forEach(cellHTML => {
          const val = document.createElement('div');
          val.className = 'sv-val';
          val.setAttribute('role', 'cell');
          val.innerHTML = cellHTML || '<span class="sv-dash">—</span>';
          rowEl.appendChild(val);
        });

        // Pad missing cells
        for (let j = row.cells.length; j < colCount; j++) {
          const val = document.createElement('div');
          val.className = 'sv-val';
          val.setAttribute('role', 'cell');
          val.innerHTML = '<span class="sv-dash">—</span>';
          rowEl.appendChild(val);
        }

        tbody.appendChild(rowEl);
      });

      body.appendChild(tbody);
      grid.appendChild(body);
      return grid;
    }

    // ── Mobile panels ───────────────────────────────────────
    _buildMobile(data) {
      const mob = document.createElement('div');
      mob.className = 'sv-mob';

      // Tab bar
      const tabBar = document.createElement('div');
      tabBar.className = 'sv-mob__tabs';

      data.products.forEach((p, i) => {
        const tab = document.createElement('div');
        tab.className   = `sv-mob__tab${i === 0 ? ' is-active' : ''}`;
        tab.dataset.idx = String(i);

        if (p.imgSrc) {
          const img = document.createElement('img');
          img.src       = p.imgSrc;
          img.alt       = p.imgAlt;
          img.className = 'sv-mob__tab-img';
          tab.appendChild(img);
        } else {
          const ph = document.createElement('div');
          ph.className = 'sv-mob__tab-img sv-mob__tab-img--placeholder';
          tab.appendChild(ph);
        }

        const name = document.createElement('span');
        name.className   = 'sv-mob__tab-name';
        name.textContent = p.title || `Product ${i + 1}`;
        tab.appendChild(name);

        tabBar.appendChild(tab);
      });

      mob.appendChild(tabBar);

      // Panels
      const panelsWrap = document.createElement('div');
      panelsWrap.className = 'sv-mob__panels';

      data.products.forEach((p, i) => {
        const panel = document.createElement('div');
        panel.className   = `sv-mob__panel${i === 0 ? ' is-active' : ''}`;
        panel.dataset.idx = String(i);

        // Price summary
        if (p.priceHTML) {
          const summary = document.createElement('div');
          summary.className = 'sv-mob__summary';
          const pr = document.createElement('div');
          pr.className = 'sv-mob__price';
          pr.innerHTML = p.priceHTML;
          summary.appendChild(pr);
          panel.appendChild(summary);
        }

        // Attribute rows
        data.rows.forEach((row, idx) => {
          const rowEl = document.createElement('div');
          rowEl.className      = 'sv-mob__row';
          rowEl.dataset.rowIdx = idx;
          if (row.hidden) rowEl.hidden = true;

          const labelEl = document.createElement('div');
          labelEl.className   = 'sv-mob__row-label';
          labelEl.textContent = row.label;
          rowEl.appendChild(labelEl);

          const valEl = document.createElement('div');
          valEl.className = 'sv-mob__row-val';
          valEl.innerHTML = row.cells[i] || '<span class="sv-dash">—</span>';
          rowEl.appendChild(valEl);

          panel.appendChild(rowEl);
        });

        // View details CTA
        const cta = document.createElement('div');
        cta.className = 'sv-mob__cta-slot is-active';
        const viewBtn = document.createElement('a');
        viewBtn.href      = p.url;
        viewBtn.className = 'btn btn--primary sv-mob__cta-view';
        viewBtn.textContent = 'View details';
        cta.appendChild(viewBtn);
        panel.appendChild(cta);

        panelsWrap.appendChild(panel);
      });

      mob.appendChild(panelsWrap);

      // Tab switching
      tabBar.addEventListener('click', evt => {
        const tab = evt.target.closest('.sv-mob__tab');
        if (!tab) return;
        const idx = tab.dataset.idx;
        tabBar.querySelectorAll('.sv-mob__tab').forEach(t => t.classList.toggle('is-active', t.dataset.idx === idx));
        panelsWrap.querySelectorAll('.sv-mob__panel').forEach(p => p.classList.toggle('is-active', p.dataset.idx === idx));
      });

      return mob;
    }

    // ── Show all / hide ─────────────────────────────────────
    _handleShowAll(evt) {
      const btn = evt.target;
      const buttonText = btn.textContent;
      btn.textContent  = btn.dataset.toggleText;
      btn.dataset.toggleText = buttonText;

      const allRows = this.querySelectorAll('.sv-row, .sv-mob__row');
      const hidden  = [...allRows].filter(r => r.hidden);

      if (hidden.length) {
        hidden.forEach(r => { r.hidden = false; r.dataset.wasHidden = 'true'; });
      } else {
        allRows.forEach(r => {
          if (r.dataset.wasHidden === 'true') {
            r.hidden = true;
            delete r.dataset.wasHidden;
          }
        });
      }
    }
  }

  customElements.define('product-comparison-grid', ProductComparisonGrid);
}